import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';

export const SubjectView = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [subjectName, setSubjectName] = useState('');

  useEffect(() => { loadChapters(); }, [subjectId]);

  const loadChapters = async () => {
    try {
      const res = await api.get(`/student/chapters/${subjectId}`);
      setChapters(res.data);
      const subRes = await api.get('/student/subjects');
      const sub = subRes.data.find(s => s.id === subjectId);
      if (sub) setSubjectName(sub.name);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Layout title={subjectName || 'Subject'}>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-3">
          <Button data-testid="back-to-dashboard" variant="ghost" onClick={() => navigate('/student')} className="rounded-full hover:bg-indigo-50">
            <ArrowLeft className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-indigo-900">{subjectName}</h1>
            <p className="text-xs sm:text-sm text-slate-500">{chapters.length} chapters</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {chapters.map((chapter, i) => (
            <motion.div key={chapter.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card data-testid={`chapter-card-${i}`}
                className="cursor-pointer hover:border-indigo-300 hover:shadow-lg hover:-translate-y-1 transition-all active:scale-95 border-slate-200"
                onClick={() => navigate(`/student/chapter/${chapter.id}`)}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm sm:text-xl shadow-md flex-shrink-0">
                      {chapter.chapter_number}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm sm:text-lg text-indigo-900 truncate">{chapter.title}</h3>
                      <p className="text-xs sm:text-sm text-slate-500">Chapter {chapter.chapter_number}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {chapters.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No chapters available yet</p>
          </div>
        )}
      </div>
    </Layout>
  );
};
